import { Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { CustomValue } from '@/console/features/jobs/components/jobs-list/jobs-list-renderer.styled';
import { TJobRenderComponentProps } from '@/console/features/jobs/components/jobs-list/jobs-list-renderer.types';
import { ExecuteIcon } from '@/shared/assets/icons/execute-icon';
import { PipelinesIcon } from '@/shared/assets/icons/pipelines-icon';
import { Skeleton } from '@/shared/components/ui/skeleton/skeleton.styled';
import { Tooltip } from '@/shared/components/ui/tooltip/tooltip';
import { TruncatedText } from '@/shared/components/ui/truncated-text/truncated-text';
import { usePermissions } from '@/shared/features/ability/hooks/use-permissions/use-permissions';
import { useReadPipelineQuery } from '@/shared/redux/api/jobs/jobs-api';
import { JobSourceEnum } from '@/shared/types/types.generated';

export const JobSource: React.FC<TJobRenderComponentProps> = ({
  job,
  truncate,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canReadPipeline = usePermissions({
    entity: 'Pipeline',
    action: 'read',
    attribute: { id: job?.pipelineConfigurationId },
  });
  const { data: pipelineData, isLoading: pipelineLoading } =
    useReadPipelineQuery(
      { pipelineConfigurationId: job?.pipelineConfigurationId! },
      {
        skip:
          !job?.pipelineConfigurationId ||
          job?.source !== JobSourceEnum.Pipeline ||
          !canReadPipeline,
      },
    );

  const pipeline = pipelineData?.readPipelineConfiguration;

  const navigateToPipeline = () => {
    if (!pipeline) {
      return undefined;
    }
    navigate(`/pipelines/${job?.pipelineConfigurationId}`);
  };

  const getSourceName = () => {
    if (
      job?.source === JobSourceEnum.Pipeline ||
      job?.pipelineConfigurationId
    ) {
      return (
        pipeline?.name ??
        (canReadPipeline
          ? t('pipelines:deleted_pipeline')
          : t('pipelines:restricted_pipeline'))
      );
    }

    if (!job?.source) {
      return t('common:manual');
    }

    return t(`pipelines:job_source.${job?.source.toLowerCase()}`);
  };

  const renderLabel = () => {
    const label = getSourceName();

    if (truncate) {
      return <TruncatedText variant="p7" maxLength={32} text={label} />;
    }

    return <Typography variant="p7">{label}</Typography>;
  };

  const getTooltipTitle = () => {
    if (pipeline?.name && (!truncate || getSourceName()?.length <= 32)) {
      return t('pipelines:open_pipeline');
    }
    return '';
  };

  if (pipelineLoading) {
    return <Skeleton />;
  }

  return (
    <Tooltip title={getTooltipTitle()}>
      <CustomValue onClick={navigateToPipeline} $clickable={!!pipeline}>
        {!job?.pipelineConfigurationId ? (
          <ExecuteIcon size="1rem" variant="primary" />
        ) : (
          <PipelinesIcon
            size="1rem"
            variant={canReadPipeline ? 'primary' : 'light'}
          />
        )}
        {renderLabel()}
      </CustomValue>
    </Tooltip>
  );
};
